import { useState } from "react";
import { useSelector } from "react-redux";
import { getIsPromptSupportOptions } from "redux/SingleDataset/selectors";
import classNames from "classnames";
import { EnrichmentModelForPreview } from "types";
import SingleModel from "./SingleModel";

import styles from "./style.module.scss";

const ModelList = ({
  isOwner,
  onSelectModel,
  onRemoveModel,
  previewModels,
  selectedModelId,
  isPublicDataset,
  onShowModelOptions,
  onStartEnrichment,
  hasOnlyEnrichedModels,
  allEnrichedModelsUsed,
}: ModelListProps) => {
  const [isHoveringEnrichButton, setIsHoveringEnrichButton] = useState(false);
  const modelsNotAvaiable = previewModels.length < 1;
  const promptSupportEnabled = useSelector((state) =>
    getIsPromptSupportOptions(state)
  ).enabled;
  const missingPrompt =
    promptSupportEnabled &&
    previewModels.some(
      (model) =>
        model.vqa_prompt &&
        (model.metadata === null ||
          model.metadata.prompt === null ||
          model.metadata.prompt.length === 0)
    );
  const hiddenButton =
    !isOwner ||
    modelsNotAvaiable ||
    hasOnlyEnrichedModels ||
    allEnrichedModelsUsed ||
    missingPrompt;
  const generateButtonClassnames = classNames(styles.generateModalButton, {
    [styles.disabledGenerateBtn]: hiddenButton,
  });

  const disabledReason = !isOwner
    ? isPublicDataset
      ? "Enrichment is unavailable for public datasets"
      : "Enrichment is unavailable for this dataset"
    : hasOnlyEnrichedModels
    ? "Please add more models to enrich the dataset"
    : missingPrompt
    ? "Please add prompt to all models"
    : allEnrichedModelsUsed
    ? "All available models have been enriched, the new models will be available soon."
    : "";

  const modelsContainerClassnames = classNames(styles.modelsContainer, {
    [styles.removePadding]: modelsNotAvaiable,
  });

  const headerClassnames = classNames(styles.header, {
    [styles.borderRadius]: modelsNotAvaiable,
  });

  return (
    <div className={styles.modelsContainerWrapper}>
      <div className={modelsContainerClassnames}>
        <div className={headerClassnames}>
          <div className={styles.selectedModalText}>
            Models {!modelsNotAvaiable && `(${previewModels.length})`}
          </div>

          <div
            onClick={onShowModelOptions}
            className={styles.addModelButton}
            data-testid="enrichment-page-add-model-btn"
          >
            Add Model
          </div>
          <div
            className={styles.generateWrapper}
            onMouseOver={() => setIsHoveringEnrichButton(true)}
            onMouseLeave={() => setIsHoveringEnrichButton(false)}
          >
            <div
              onClick={onStartEnrichment}
              className={generateButtonClassnames}
              data-testid={"models-list-enrich-dataset-btn"}
            >
              Enrich Dataset
            </div>

            {isHoveringEnrichButton &&
              hiddenButton &&
              disabledReason.length > 0 && (
                <div className={styles.allmodelsUsedPopup}>
                  {disabledReason}
                </div>
              )}
          </div>
        </div>
        {previewModels.length > 0 && (
          <div className={styles.selectedModalList}>
            {previewModels.map((model: EnrichmentModelForPreview, index) => {
              const isSelected = model.id === selectedModelId;
              const missingPrompt =
                promptSupportEnabled &&
                model.vqa_prompt &&
                !model.metadata?.prompt?.length;
              return (
                <SingleModel
                  key={index}
                  model={model}
                  isSelected={isSelected}
                  onRemoveModel={onRemoveModel}
                  onSelectModel={onSelectModel}
                  missingPrompt={missingPrompt}
                />
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default ModelList;

interface ModelListProps {
  isOwner: boolean;
  isPublicDataset: boolean;
  previewModels: EnrichmentModelForPreview[];
  selectedModelId: string | null;
  hasOnlyEnrichedModels: boolean;
  onShowModelOptions: () => void;
  onStartEnrichment: () => void;
  allEnrichedModelsUsed: boolean;
  onSelectModel: (modelId: string, newPrompt?: boolean) => void;
  onRemoveModel: (modelId: string) => void;
}
